import React, { useEffect, useMemo, useState } from "react";
import {
	Modal,
	Select,
	Table,
	Checkbox,
	Button,
	Space,
	Typography,
	InputNumber,
	message,
} from "antd";
import { BulbOutlined } from "@ant-design/icons";
import Image from "../../../../../../common/image/Image";
import { COLORS } from "../../../../../../layout/theme";
import { FaImages } from "react-icons/fa";
import { CONSTANTS } from "../../../../../../../utils/constants";
import { AntdTable, TableContainer } from "./styles";

const { Text } = Typography;

export default function SuggestedProductsModal({
	open,
	onClose,
	laboratoryId,
	clientInfo,
	fetchPointsOfSale,
	fetchSuggestedByPointOfSale,
	onAccept,
}) {
	const [loadingPos, setLoadingPos] = useState(false);
	const [loadingSuggested, setLoadingSuggested] = useState(false);

	const [pointsOfSale, setPointsOfSale] = useState([]);
	const [selectedPosId, setSelectedPosId] = useState(null);
	const [suggested, setSuggested] = useState([]);

	const [selectedMap, setSelectedMap] = useState({});
	const [qtyMap, setQtyMap] = useState({});

	const selectedIds = useMemo(
		() => Object.keys(selectedMap).filter((k) => !!selectedMap[k]),
		[selectedMap],
	);

	const allChecked =
		suggested.length > 0 && selectedIds.length === suggested.length;

	const someChecked =
		selectedIds.length > 0 && selectedIds.length < suggested.length;

	useEffect(() => {
		if (!open) return;

		const loadPointsOfSale = async () => {
			try {
				setLoadingPos(true);

				const response = await fetchPointsOfSale(clientInfo?.userId);

				const list = response?.pointsOfSale || response || [];

				setPointsOfSale(list);

				if (list.length === 1) {
					setSelectedPosId(list[0].id);
				} else {
					setSelectedPosId(null);
				}
			} catch (error) {
				message.error("No se pudieron cargar los puntos de venta.");
			} finally {
				setLoadingPos(false);
			}
		};

		loadPointsOfSale();
	}, [open, clientInfo, laboratoryId, fetchPointsOfSale]);

	useEffect(() => {
		if (!open || !selectedPosId) {
			setSuggested([]);
			setSelectedMap({});
			setQtyMap({});
			return;
		}

		const loadSuggested = async () => {
			try {
				setLoadingSuggested(true);

				const response = await fetchSuggestedByPointOfSale(
					laboratoryId,
					CONSTANTS.PAGE_LARGE_SIZE,
					selectedPosId,
				);

				const list = response?.suggesteds || response || [];

				setSuggested(list);

				const nextSelectedMap = {};
				const nextQtyMap = {};

				list.forEach((item) => {
					const productId = String(item.productId || item.id);

					nextSelectedMap[productId] = true;

					const suggestedQty = Number(item.suggestedQty);
					nextQtyMap[productId] =
						Number.isFinite(suggestedQty) && suggestedQty > 0
							? suggestedQty
							: 1;
				});

				setSelectedMap(nextSelectedMap);
				setQtyMap(nextQtyMap);
			} catch (error) {
				message.error("No se pudieron cargar los productos sugeridos.");
			} finally {
				setLoadingSuggested(false);
			}
		};

		loadSuggested();
	}, [
		open,
		selectedPosId,
		laboratoryId,
		clientInfo,
		fetchSuggestedByPointOfSale,
	]);

	const columns = [
		{
			title: (
				<Checkbox
					indeterminate={someChecked}
					checked={allChecked}
					onChange={(e) => {
						const checked = e.target.checked;
						const next = {};

						suggested.forEach((item) => {
							const productId = String(item.productId || item.id);
							next[productId] = checked;
						});

						setSelectedMap(next);
					}}
				/>
			),
			dataIndex: "productId",
			width: 50,
			render: (_, row) => {
				const productId = String(row.productId || row.id);

				return (
					<Checkbox
						checked={!!selectedMap[productId]}
						onChange={(e) => {
							setSelectedMap((prev) => ({
								...prev,
								[productId]: e.target.checked,
							}));
						}}
					/>
				);
			},
		},
		{
			title: (
				<div style={{ fontSize: 18 }}>
					<FaImages style={{ color: COLORS.White }} />
				</div>
			),
			dataIndex: "photoUrl",
			width: 90,
			align: "center",
			render: (value, record) => (
				<Image width={40} height={40} src={value} alt={record.title} />
			),
		},
		{
			title: <div style={{ color: COLORS.White }}>Producto</div>,
			dataIndex: "title",
			render: (value, record) => (
				<div style={{ display: "flex", flexDirection: "column" }}>
					<Text strong>{value}</Text>
					{record.brandName && <Text type="secondary">{record.brandName}</Text>}
				</div>
			),
		},
		{
			title: <div style={{ color: COLORS.White }}>EAN</div>,
			dataIndex: "ean13",
			width: 160,
			render: (value) => <Text>{value}</Text>,
		},
		{
			title: <div style={{ color: COLORS.White }}>Sugerida</div>,
			dataIndex: "suggestedQty",
			width: 100,
			render: (value) => <Text>{value || 1}</Text>,
		},
		{
			title: <div style={{ color: COLORS.White }}>Cantidad</div>,
			dataIndex: "productId",
			width: 150,
			render: (_, row) => {
				const productId = String(row.productId || row.id);
				const isSelected = !!selectedMap[productId];

				const fallbackQty = Number(row.suggestedQty);
				const value =
					qtyMap[productId] ??
					(Number.isFinite(fallbackQty) && fallbackQty > 0 ? fallbackQty : 1);

				return (
					<InputNumber
						min={1}
						step={1}
						precision={0}
						value={value}
						disabled={!isSelected}
						style={{ width: 110, borderRadius: 15 }}
						onChange={(val) => {
							const nextValue = val == null ? 1 : Number(val);

							setQtyMap((prev) => ({
								...prev,
								[productId]: nextValue,
							}));

							if (!selectedMap[productId]) {
								setSelectedMap((prev) => ({
									...prev,
									[productId]: true,
								}));
							}
						}}
					/>
				);
			},
		},
	];

	const handleAccept = () => {
		if (!selectedPosId) {
			message.warning("Seleccioná un punto de venta.");
			return;
		}

		const selectedProducts = suggested.filter((item) => {
			const productId = String(item.productId || item.id);
			return !!selectedMap[productId];
		});

		if (selectedProducts.length === 0) {
			message.warning("Seleccioná al menos un producto sugerido.");
			return;
		}

		const invalidProduct = selectedProducts.find((item) => {
			const productId = String(item.productId || item.id);
			const qty = Number(qtyMap[productId]);

			return !Number.isFinite(qty) || qty < 1;
		});

		if (invalidProduct) {
			message.warning("Revisá las cantidades. El mínimo es 1.");
			return;
		}

		const productsToApply = selectedProducts.map((item) => {
			const productId = String(item.productId || item.id);

			return {
				...item,
				id: item.productId || item.id,
				productId: item.productId || item.id,
				quantity: Number(qtyMap[productId]),
			};
		});

		onAccept(productsToApply);
		onClose();
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title={
				<Space>
					<BulbOutlined style={{ color: COLORS.Secondary }} />
					<span>Productos sugeridos</span>
				</Space>
			}
			width={950}
			destroyOnClose
			footer={
				<Space>
					<Button onClick={onClose}>Cerrar</Button>
					<Button
						type="primary"
						onClick={handleAccept}
						disabled={loadingPos || loadingSuggested}
					>
						Agregar a la lista
					</Button>
				</Space>
			}
		>
			<Space direction="vertical" style={{ width: "100%" }} size={12}>
				<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
					<Text strong>
						Cliente: {clientInfo?.clientName || "Sin cliente seleccionado"}
					</Text>

					<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
						<Text strong>Punto de venta:</Text>

						<Select
							style={{ width: 420 }}
							loading={loadingPos}
							value={selectedPosId}
							placeholder="Seleccioná un punto de venta"
							onChange={setSelectedPosId}
							disabled={pointsOfSale.length <= 1}
							options={pointsOfSale.map((pointOfSale) => ({
								value: pointOfSale.id,
								label: `${pointOfSale.name}${
									pointOfSale.clientName ? ` — ${pointOfSale.clientName}` : ""
								}`,
							}))}
						/>
					</div>
				</div>

				<TableContainer containerBackground={COLORS.White}>
					<AntdTable
						rowKey={(row) => String(row.productId || row.id)}
						columns={columns}
						dataSource={suggested}
						loading={loadingSuggested}
						pagination={{ pageSize: 8 }}
						size="middle"
					/>
				</TableContainer>
			</Space>
		</Modal>
	);
}
